import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PageTransition } from "@/components/PageTransition";
import { BackToTop } from "@/components/BackToTop";
import { SkipLink } from "@/components/SkipLink";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { lazy, Suspense, useEffect } from "react";
import Index from "./pages/Index";
import { CookieBanner } from "./components/CookieBanner";
import { ScrollToTop } from "./components/ScrollToTop";
import { useGTM } from "./hooks/useGTM";
import { useClarity } from "./hooks/useClarity";

// Lazy load public pages (not needed on first paint)
const Excursions = lazy(() => import("./pages/Excursions"));
const ExcursionDetail = lazy(() => import("./pages/ExcursionDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Avis = lazy(() => import("./pages/Avis"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminExcursions = lazy(() => import("./pages/AdminExcursions"));
const AdminExcursionEdit = lazy(() => import("./pages/AdminExcursionEdit"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminBlogEdit = lazy(() => import("./pages/AdminBlogEdit"));
const AdminSiteImages = lazy(() => import("./pages/AdminSiteImages"));
const AdminAssoGallery = lazy(() => import("./pages/AdminAssoGallery"));
const AdminSocialPolaroids = lazy(() => import("./pages/AdminSocialPolaroids"));

const queryClient = new QueryClient();

/**
 * Fallback discret : une fine barre de progression en haut de l'écran.
 * Pas de splash plein écran → plus de sensation de "page qui disparaît".
 */
const PageFallback = () => (
  <div
    aria-label="Chargement"
    className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-roca-orange/80 animate-pulse"
  />
);

// Préchargement des routes en idle pour éviter le délai au clic
const prefetchRoutes = () => {
  const idle = (cb: () => void) =>
    'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(cb)
      : setTimeout(cb, 1500);
  idle(() => {
    import("./pages/Excursions");
    import("./pages/About");
    import("./pages/Contact");
    import("./pages/Avis");
    import("./pages/Blog");
  });
};

const AppContent = () => {
  useGTM();
  useClarity();
  useEffect(() => { prefetchRoutes(); }, []);

  return (
    <>
      <SkipLink />
      <ScrollToTop />
      <BackToTop />
      <MobileBottomNav />
      <Routes>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/excursions" element={<PageTransition><Suspense fallback={<PageFallback />}><Excursions /></Suspense></PageTransition>} />
        <Route path="/excursions/:id" element={<PageTransition><Suspense fallback={<PageFallback />}><ExcursionDetail /></Suspense></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Suspense fallback={<PageFallback />}><Contact /></Suspense></PageTransition>} />
        <Route path="/a-propos" element={<PageTransition><Suspense fallback={<PageFallback />}><About /></Suspense></PageTransition>} />
        <Route path="/avis" element={<PageTransition><Suspense fallback={<PageFallback />}><Avis /></Suspense></PageTransition>} />
        <Route path="/blog" element={<PageTransition><Suspense fallback={<PageFallback />}><Blog /></Suspense></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><Suspense fallback={<PageFallback />}><BlogPost /></Suspense></PageTransition>} />
        <Route path="/admin-login" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminLogin /></Suspense></PageTransition>} />
        <Route path="/admin" element={<PageTransition><Suspense fallback={<PageFallback />}><Admin /></Suspense></PageTransition>} />
        <Route path="/admin/testimonials" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminTestimonials /></Suspense></PageTransition>} />
        <Route path="/admin/excursions" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminExcursions /></Suspense></PageTransition>} />
        <Route path="/admin/excursions/new" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminExcursionEdit /></Suspense></PageTransition>} />
        <Route path="/admin/excursions/:id/edit" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminExcursionEdit /></Suspense></PageTransition>} />
        <Route path="/admin/users" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminUsers /></Suspense></PageTransition>} />
        <Route path="/admin/blog" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminBlog /></Suspense></PageTransition>} />
        <Route path="/admin/blog/new" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminBlogEdit /></Suspense></PageTransition>} />
        <Route path="/admin/blog/:id/edit" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminBlogEdit /></Suspense></PageTransition>} />
        <Route path="/admin/site-images" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminSiteImages /></Suspense></PageTransition>} />
        <Route path="/admin/asso-gallery" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminAssoGallery /></Suspense></PageTransition>} />
        <Route path="/admin/social-polaroids" element={<PageTransition><Suspense fallback={<PageFallback />}><AdminSocialPolaroids /></Suspense></PageTransition>} />
        <Route path="/mentions-legales" element={<PageTransition><Suspense fallback={<PageFallback />}><MentionsLegales /></Suspense></PageTransition>} />
        <Route path="/confidentialite" element={<PageTransition><Suspense fallback={<PageFallback />}><PolitiqueConfidentialite /></Suspense></PageTransition>} />
        <Route path="*" element={<PageTransition><Suspense fallback={<PageFallback />}><NotFound /></Suspense></PageTransition>} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <Toaster />
      <Sonner />
      <CookieBanner />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
